import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { User, UserRole } from '../types';
import { API_ENDPOINTS, ROLE_PRIORITIES } from '../constants';

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shouldRefetch, setShouldRefetch] = useState(true);

  const getRolePriority = useCallback((role: string): number => {
    return ROLE_PRIORITIES[role as keyof typeof ROLE_PRIORITIES] || 6;
  }, []);

  const sortUsers = useCallback((usersToSort: User[]): User[] => {
    return [...usersToSort].sort((a, b) => {
      const rolePriorityA = getRolePriority(a.role);
      const rolePriorityB = getRolePriority(b.role);
      
      if (rolePriorityA !== rolePriorityB) {
        return rolePriorityA - rolePriorityB;
      }
      
      if (a.role === 'user' && b.role === 'user') {
        const numA = parseInt(a.pharmacyNum);
        const numB = parseInt(b.pharmacyNum);
        return numA - numB;
      }
      
      return a.id - b.id;
    });
  }, [getRolePriority]);

  const fetchUsers = useCallback(async () => {
    if (!shouldRefetch) return;

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(API_ENDPOINTS.USERS);
      const sortedUsers = sortUsers(response.data);
      setUsers(sortedUsers);
      setShouldRefetch(false);
    } catch (error) {
      console.error('Ошибка при получении списка пользователей:', error);
      setError('Не удалось загрузить список пользователей');
    } finally {
      setLoading(false);
    }
  }, [shouldRefetch, sortUsers]);

  const refetchUsers = useCallback(() => {
    setShouldRefetch(true);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    refetchUsers,
    sortUsers
  };
};

